/**
 * An indeterminate progress indicator -- a small rotating arc that says "work is happening" without
 * claiming to know how much is left.
 *
 * NAMING WARNING: `Spinner` (from `react-vcomponents`) is NOT this. It is a NUMBER-INPUT spinner -- the
 * little up/down arrows on a numeric field -- so using it as a busy indicator renders an empty number box,
 * which is what it did in the Echo Hunt dev panels before this component existed. Reach for
 * `ProgressSpinner` for "an async process is running", and `Spinner` only for a number the user can nudge.
 *
 * Deliberately indeterminate: the phases that use it (LLM calls, corpus indexing) cannot report a
 * trustworthy fraction, and a fake progress bar is worse than none. Where a real fraction IS available,
 * pass `progress` (0..1) to draw it as a ring on top of the rotation -- the caller decides, this
 * component does not invent one.
 *
 * The rotation is a CSS keyframe animation registered once via `AddGlobalStyle`, so N spinners on a page
 * cost one stylesheet rule and no timers. `prefers-reduced-motion` falls back to a static arc rather
 * than nothing, since a still-visible indicator still communicates "busy".
 */
import React from "react";
import {AddGlobalStyle} from "react-vextensions";
import {E} from "js-vextensions";

export type ProgressSpinnerProps = {
	/** diameter in pixels. Default 16 -- roughly the height of body text, so it aligns with a label. */
	size?: number|n,
	/** stroke width in pixels. Default 2. */
	thickness?: number|n,
	/** arc colour. Default a neutral light grey that reads on the app's dark panels. */
	color?: string|n,
	/** track colour (the faint full ring behind the arc). Default a translucent white. */
	trackColor?: string|n,
	/** rotation speed in seconds per turn. Default 1s. */
	duration?: number|n,
	/** 0..1 -- when known, the ring is drawn to this fraction instead of spinning indeterminately. */
	progress?: number|n,
	/** accessible label. Default "Loading". */
	title?: string|n,
	/** spacing helpers, since this is usually inline next to a label or a button. */
	ml?: number|n, mr?: number|n, mt?: number|n, mb?: number|n,
	style?: React.CSSProperties|n,
	className?: string|n,
};

// registered once per page load; `AddGlobalStyle` is idempotent by class name here because the keyframes
// name is fixed and re-inserting an identical @keyframes rule is harmless
AddGlobalStyle(`
@keyframes lf-progress-spinner-spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
	.lf-progress-spinner-rotating { animation: none !important; }
}
`);

export const ProgressSpinner = (props: ProgressSpinnerProps)=>{
	let {
		size, thickness, color, trackColor, duration, progress, title,
		ml, mr, mt, mb, style, className,
	} = props;

	// `n` is `null | undefined`, so a destructuring default would not cover the `null` case
	size ??= 16;
	thickness ??= 2;
	color ??= "rgba(235,235,235,.85)";
	trackColor ??= "rgba(255,255,255,.15)";
	duration ??= 1;
	title ??= "Loading";

	const clampedThickness = Math.max(1, Math.min(thickness, size / 2 - 1));
	const radius = (size - clampedThickness) / 2;
	const circumference = 2 * Math.PI * radius;

	// when a real fraction is known, draw that much of the ring and stop spinning; otherwise leave a short
	// arc spinning, which is the conventional indeterminate look
	const determinate = progress != null && Number.isFinite(progress);
	const fraction = determinate ? Math.max(0, Math.min(1, progress)) : .25;
	const center = size / 2;
	const cssClassName = ["lf-progress-spinner", !determinate && "lf-progress-spinner-rotating", className].filter(a=>a != null).join(" ");

	return (
		<svg
			width={size} height={size}
			viewBox={`0 0 ${size} ${size}`}
			role="progressbar"
			aria-label={title}
			aria-valuemin={determinate ? 0 : undefined}
			aria-valuemax={determinate ? 100 : undefined}
			aria-valuenow={determinate ? Math.round(fraction * 100) : undefined}
			className={cssClassName}
			style={E(
				ml != null && {marginLeft: ml},
				mr != null && {marginRight: mr},
				mt != null && {marginTop: mt},
				mb != null && {marginBottom: mb},
				{flex: "none", display: "inline-block", verticalAlign: "middle"},
				!determinate && {animation: `lf-progress-spinner-spin ${duration}s linear infinite`},
				style,
			)}>
			{/* the track: a faint full ring, so the arc has something to sit on (and so the element still
			    reads as a ring on a light background) */}
			<circle
				cx={center} cy={center} r={radius}
				fill="none" stroke={trackColor} strokeWidth={clampedThickness}/>
			{/* the arc. Drawn as a dashed stroke of `fraction` of the circumference, so one code path serves
			    both the indeterminate and the determinate case */}
			<circle
				cx={center} cy={center} r={radius}
				fill="none" stroke={color} strokeWidth={clampedThickness}
				strokeLinecap={determinate ? "butt" : "round"}
				strokeDasharray={`${circumference * fraction} ${circumference}`}
				// start the arc at 12 o'clock instead of 3 o'clock
				transform={`rotate(-90 ${center} ${center})`}/>
		</svg>
	);
};
